import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import type { Content } from '@plone/types';
import { getContent } from '@plone/volto/actions';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import { flattenToAppURL } from '@plone/volto/helpers';
import { Sidepanel } from '@designsystem/stories/04-organisms/Sidepanel';
import { Loader } from '@designsystem/stories/02-atoms/Loader';
import HtmlIframe from '@fhnw/components/HtmlIframe/HtmlIframe';

import {
  PANEL_ID,
  useOccasionRegistration,
  type RegistrationOccasion,
} from './OccasionRegistrationContext';
import { messages } from './messages';

const initialState = {
  data: [] as unknown as Content,
  loading: false,
  loaded: false,
};

/**
 * Fetches the CMS form page for an occasion and renders its blocks.
 * Keyed by the occasion UID so concurrent occasions never collide.
 */
const OccasionFormContent = ({
  occasion,
}: {
  occasion: RegistrationOccasion;
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const intl = useIntl();
  const subrequest = `occ-form-${occasion.UID}`;
  const path = flattenToAppURL(occasion.cms_form_path as string);

  const {
    data,
    loading,
    loaded,
    error,
  }: {
    data: Content;
    loading: boolean;
    loaded: boolean;
    error?: unknown;
  } = useSelector(
    (state: {
      content?: { subrequests?: Record<string, typeof initialState> };
    }) => state.content?.subrequests?.[subrequest] || initialState,
  ) as any;

  useEffect(() => {
    // Stop on error too — otherwise a failed subrequest (loading:false,
    // loaded:false) keeps re-dispatching getContent and the loader never
    // resolves (#1663 review).
    if (loaded || loading || error) {
      return;
    }
    dispatch(getContent(path, '', subrequest) as any);
  }, [dispatch, path, subrequest, loaded, loading, error]);

  if (error) {
    return (
      <p className="occasion-registration__error" role="alert">
        {intl.formatMessage(messages.FormLoadError)}
      </p>
    );
  }

  if (!loaded) {
    return <Loader />;
  }

  // The hidden occasion fields are only injected into a schemaForm that is
  // marked eventRegistration:true. If the editor pointed cms_form_path at a
  // page without such a form, the form would render but silently drop the
  // occasion data — so surface a clear error instead (issue #1663).
  const blocks = (data?.blocks ?? {}) as Record<
    string,
    { '@type'?: string; eventRegistration?: boolean }
  >;
  const hasRegistrationForm = Object.values(blocks).some(
    (block) =>
      block?.['@type'] === 'schemaForm' && block?.eventRegistration === true,
  );
  if (!hasRegistrationForm) {
    return (
      <p className="occasion-registration__error" role="alert">
        {intl.formatMessage(messages.NoRegistrationForm)}
      </p>
    );
  }

  return (
    <RenderBlocks
      location={{ ...location, pathname: flattenToAppURL(data['@id']) }}
      content={data}
    />
  );
};

/**
 * The single, globally-mounted registration sidepanel. Reads the active
 * occasion from the registration context and renders an occasion-only header
 * plus either the CRM embed or the CMS form page.
 */
const OccasionRegistrationPanel = () => {
  const intl = useIntl();
  const { payload, hidden } = useOccasionRegistration();
  const event = payload?.event;
  const occasion = payload?.occasion;
  const isCrmForm = Boolean(
    occasion?.crm_application && occasion?.crm_embed_code,
  );

  return (
    <Sidepanel id={PANEL_ID} close={intl.formatMessage(messages.Close)}>
      {occasion && (
        <div className="occasion-registration">
          <div className="occasion-registration__header">
            {event?.title && <h2 className="h1">{event.title}</h2>}
            {/* Durchführungsname in the page intro-lead style (#1663 mockup). */}
            {occasion.occasion_title && (
              <p className="text--lead">{occasion.occasion_title}</p>
            )}
            {/* CRM form scripts render the occasion date themselves, so the
                header must not duplicate it for CRM occasions. */}
            {!isCrmForm && hidden.datum && (
              <p>
                <strong>{intl.formatMessage(messages.DateLabel)}:</strong>{' '}
                {hidden.datum}
              </p>
            )}
            {occasion.place_short && (
              <p>
                <strong>{intl.formatMessage(messages.PlaceLabel)}:</strong>{' '}
                {occasion.place_short}
              </p>
            )}
          </div>
          <div className="occasion-registration__content">
            {occasion.crm_application && occasion.crm_embed_code ? (
              <HtmlIframe content={occasion.crm_embed_code} />
            ) : occasion.cms_application && occasion.cms_form_path ? (
              <OccasionFormContent occasion={occasion} />
            ) : null}
          </div>
        </div>
      )}
    </Sidepanel>
  );
};

export default OccasionRegistrationPanel;
